import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Button, Typography, Stack, CircularProgress, Paper, Avatar, Chip, IconButton, Autocomplete, TextField, Snackbar, Alert } from '@mui/material';
import { GridView, Add, DeleteOutline, BusinessCenter, Close } from '@mui/icons-material';
import { getData, postData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';

function AssignProjectTeam({ project, projectId: explicitProjectId, onClose, onTeamAssignSuccess, onSuccess }) {
    const [loading, setLoading] = useState(true);
    const [existingTeamsData, setExistingTeamsData] = useState([]);
    const [editedExistingTeams, setEditedExistingTeams] = useState({});
    const [departments, setDepartments] = useState([]);
    const [newAssignments, setNewAssignments] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [initialExistingTeams, setInitialExistingTeams] = useState("");
    const resolvedProjectId = useMemo(
        () => project?.id || project?.project?.id || explicitProjectId,
        [project?.id, project?.project?.id, explicitProjectId]
    );

    const hasChanges = () => {
        const hasNewAssignments = newAssignments.length > 0;
        const existingTeamsChanged = JSON.stringify(editedExistingTeams) !== initialExistingTeams;
        return hasNewAssignments || existingTeamsChanged;
    };

    const fetchProjectDetails = useCallback(async () => {
        if (!resolvedProjectId) {
            console.warn('Cannot fetch project details - project.id is missing:', resolvedProjectId);
            return;
        }
        try {
            console.log('Fetching project details for project ID:', resolvedProjectId);
            const response = await getData(API_ENDPOINTS.fetchProjectById(resolvedProjectId));
            const resData = response.data || response;

            console.log('Project details response:', resData);

            if (resData.status === 'SUCCESS') {
                const projectData = resData.message.data;
                if (projectData?.departments && projectData.departments.length > 0) {
                    const allExistingTeams = [];
                    const initialEditedTeams = {};

                    projectData.departments.forEach(department => {
                        if (department.teams) {
                            department.teams.forEach(team => {
                                const compositeKey = `${department.department_id}-${team.team_id}`;

                                const formattedMembers = team.members ? team.members.map(member => ({
                                    id: member.id,
                                    firstName: member.first_name,
                                    lastName: member.last_name,
                                })) : [];

                                allExistingTeams.push({
                                    team_id: team.team_id,
                                    team_name: team.team_name,
                                    department_id: department.department_id,
                                    department_name: department.department_name,
                                    compositeKey: compositeKey
                                });

                                initialEditedTeams[compositeKey] = formattedMembers;
                            });
                        }
                    });

                    setExistingTeamsData(allExistingTeams);
                    setEditedExistingTeams(initialEditedTeams);
                    setInitialExistingTeams(JSON.stringify(initialEditedTeams));
                } else {
                    console.log('No departments found in project data');
                    setExistingTeamsData([]);
                    setEditedExistingTeams({});
                    setInitialExistingTeams('{}');
                }
            }
        } catch (error) {
            console.error('Error fetching project details:', error);
            setExistingTeamsData([]);
            setEditedExistingTeams({});
        }
    }, [resolvedProjectId]);

    useEffect(() => {
        const loadInitialData = async () => {
            if (!resolvedProjectId) {
                console.warn('Project or project.id is missing:', resolvedProjectId);
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                await fetchProjectDetails();
                const deps = await fetchDepartments();
                if (deps) setDepartments(deps);
            } catch (error) {
                console.error('Failed to load initial data:', error);
            } finally {
                setLoading(false);
            }
        };
        loadInitialData();
    }, [resolvedProjectId, fetchProjectDetails]);

    const fetchDepartments = async () => {
        const userDataRaw = localStorage.getItem('userData');
        const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
        const organizationId = userData?.organization_id;
        try {
            const response = await getData(`${API_ENDPOINTS.fetchDepartmentsByOrganizationId}/${organizationId}`);
            return response.message?.departments || [];
        } catch (error) {
            return [];
        }
    };

    const fetchTeamsData = async (departmentId) => {
        try {
            const response = await getData(`${API_ENDPOINTS.fetchTeamsByDepartmentId}/${departmentId}`);
            const teamsList = response.message?.teams || [];
            return teamsList.map((team) => ({ id: team.id, name: team.team }));
        } catch (error) {
            return [];
        }
    };

    const fetchAllTeamMembers = async (teamId) => {
        try {
            const response = await getData(`${API_ENDPOINTS.fetchUsersByTeamId}/${teamId}`);
            return response?.message?.users?.map((user) => ({
                id: user.id,
                firstName: user.first_name,
                lastName: user.last_name
            })) || [];
        } catch (error) {
            return [];
        }
    };

    const handleAddNewRow = () => {
        setNewAssignments([...newAssignments, {
            tempId: Date.now(),
            dept: null,
            team: null,
            members: [],
            availableTeams: [],
            availableMembers: []
        }]);
    };

    const handleRemoveNewRow = (tempId) => {
        setNewAssignments(newAssignments.filter(item => item.tempId !== tempId));
    };

    const handleDeptChange = async (tempId, newValue) => {
        const teams = newValue ? await fetchTeamsData(newValue.id) : [];
        setNewAssignments(prev => prev.map(item =>
            item.tempId === tempId ? { ...item, dept: newValue, team: null, members: [], availableTeams: teams, availableMembers: [] } : item
        ));
    };

    const handleTeamChange = async (tempId, newValue) => {
        let members = [];
        if (newValue) members = await fetchAllTeamMembers(newValue.id);
        setNewAssignments(prev => prev.map(item =>
            item.tempId === tempId ? { ...item, team: newValue, members: [], availableMembers: members } : item
        ));
    };

    const handleMemberChange = (tempId, newValue) => {
        setNewAssignments(prev => prev.map(item =>
            item.tempId === tempId ? { ...item, members: newValue } : item
        ));
    };

    const handleRemoveMember = (compositeKey, memberId) => {
        setEditedExistingTeams(prev => ({
            ...prev,
            [compositeKey]: prev[compositeKey].filter(m => m.id !== memberId)
        }));
    };

    const handleAssignTeams = async () => {
        if (!hasChanges()) {
            setSnackbar({ open: true, message: 'No changes detected to save.', severity: 'info' });
            return;
        }

        const finalPayload = [];

        // 1. Process Existing Assignments (using compositeKey)
        Object.keys(editedExistingTeams).forEach(key => {
            const [deptId, teamId] = key.split('-').map(Number);
            editedExistingTeams[key].forEach(member => {
                finalPayload.push({
                    department_id: deptId,
                    team_id: teamId,
                    user_id: member.id
                });
            });
        });

        // 2. Process New Assignments
        newAssignments.forEach(row => {
            if (row.dept && row.team && row.members.length > 0) {
                row.members.forEach(member => {
                    finalPayload.push({
                        department_id: row.dept.id,
                        team_id: row.team.id,
                        user_id: member.id
                    });
                });
            }
        });

        const payloadForAssignmentApi = {
            project_id: resolvedProjectId,
            team_members: finalPayload,
        };

        setLoading(true);
        try {
            const response = await postData(API_ENDPOINTS.updateTeamAssignment, payloadForAssignmentApi);
            if (response.data?.status === 'SUCCESS' || response.status === 'SUCCESS') {
                setSnackbar({ open: true, message: 'Teams assigned successfully!', severity: 'success' });
                await fetchProjectDetails();
                setNewAssignments([]);
                if (onTeamAssignSuccess) onTeamAssignSuccess();
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to update assignments', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    if (!resolvedProjectId) {
        return (
            <Box sx={{ p: 5, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                    No project selected for team assignment.
                </Typography>
            </Box>
        );
    }

    if (loading && existingTeamsData.length === 0) {
        return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: { xs: 0, sm: 1 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <GridView sx={{ color: '#3b82f6', fontSize: 24 }} />
                    <Typography variant="subtitle1" fontWeight="700" color="#1e293b" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        Project Team Assignment
                    </Typography>
                </Stack>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddNewRow}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1.5, sm: 2 } }}
                >
                    New Assignment
                </Button>
            </Box>

            <Stack spacing={{ xs: 1.5, sm: 2.5 }}>
                {newAssignments.map((row) => (
                    <Paper key={row.tempId} variant="outlined" sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: '16px', border: '2px solid #3b82f6', position: 'relative', bgcolor: '#f0f7ff' }}>
                        <IconButton onClick={() => handleRemoveNewRow(row.tempId)} sx={{ position: 'absolute', top: 8, right: 8 }}>
                            <Close fontSize="small" />
                        </IconButton>
                        <Typography variant="overline" color="primary" fontWeight="700" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>New Assignment Details</Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1 }}>
                            <Autocomplete
                                fullWidth
                                options={departments}
                                value={row.dept || null}
                                getOptionLabel={(opt) => opt.department || ""}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                onChange={(e, val) => handleDeptChange(row.tempId, val)}
                                renderInput={(params) => <TextField {...params} label="Select Department" size="small" />}
                            />
                            <Autocomplete
                                fullWidth
                                options={row.availableTeams || []}
                                value={row.team || null}
                                getOptionLabel={(opt) => opt.name || ""}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                disabled={!row.dept}
                                onChange={(e, val) => handleTeamChange(row.tempId, val)}
                                renderInput={(params) => <TextField {...params} label="Select Team" size="small" />}
                            />
                        </Stack>
                        <Autocomplete
                            multiple
                            sx={{ mt: 2 }}
                            options={row.availableMembers || []}
                            value={row.members || []}
                            getOptionLabel={(opt) => `${opt.firstName || ""} ${opt.lastName || ""}`}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            disabled={!row.team}
                            onChange={(e, val) => handleMemberChange(row.tempId, val)}
                            renderInput={(params) => <TextField {...params} label="Select Team Members" size="small" />}
                        />
                    </Paper>
                ))}
                {existingTeamsData.map((team) => (
                    <Paper key={team.compositeKey} variant="outlined" sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: '16px' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar sx={{ bgcolor: '#f1f5f9', color: '#6366f1', width: 42, height: 42, borderRadius: '10px' }}>
                                    <BusinessCenter fontSize="small" />
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="800" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                        {team.department_name} • {team.team_name}
                                    </Typography>
                                </Box>
                            </Stack>
                            <IconButton size="small" color="error" onClick={() => {
                                const newEdited = { ...editedExistingTeams };
                                delete newEdited[team.compositeKey];
                                setEditedExistingTeams(newEdited);
                                setExistingTeamsData(existingTeamsData.filter(t => t.compositeKey !== team.compositeKey));
                            }}>
                                <DeleteOutline fontSize="small" />
                            </IconButton>
                        </Stack>
                        <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#f8fafc', borderRadius: '12px', display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {editedExistingTeams[team.compositeKey]?.map((member) => (
                                <Chip
                                    key={member.id}
                                    label={`${member.firstName} ${member.lastName}`}
                                    onDelete={() => handleRemoveMember(team.compositeKey, member.id)}
                                    sx={{ bgcolor: '#fff', fontWeight: 600 }}
                                />
                            ))}
                        </Box>
                    </Paper>
                ))}
            </Stack>

            <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" color="inherit" onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" color="primary" onClick={handleAssignTeams} sx={{ px: 4, textTransform: 'none' }}>
                    Save Changes
                </Button>
            </Box>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default AssignProjectTeam;